import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getFollowUpAlerts } from '../../shared/api/followUps.api'

/**
 * Алерт-виджет «Связаться с клиентом» (Контроль КП, 16.07.2026): активные
 * напоминания с датой до конца сегодняшнего дня — просроченные (красные)
 * и сегодняшние (янтарные). Скоуп считает бэк: менеджер — свои заявки,
 * РОП — менеджеры своих городов, ADMIN/ГД — все. Пустой список — виджет
 * не рендерится, чтобы не занимать место на дашборде.
 */
export function FollowUpAlertsPanel({ showManager = false }: { showManager?: boolean }) {
  const { data: alerts = [] } = useQuery({
    queryKey: ['follow-up-alerts'],
    queryFn: getFollowUpAlerts,
    refetchInterval: 5 * 60 * 1000,
  })

  if (alerts.length === 0) return null

  const overdueCount = alerts.filter((a) => a.isOverdue).length

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800">🔔 Связаться с клиентом</h3>
        <span className="text-xs text-gray-400">
          {overdueCount > 0 ? `просрочено: ${overdueCount} · ` : ''}всего: {alerts.length}
        </span>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {alerts.map((a) => (
          <Link
            key={a.id}
            to={`/requests/${a.requestId}`}
            className={`block rounded-lg border px-3 py-2 transition-colors ${
              a.isOverdue
                ? 'bg-red-50 border-red-200 hover:bg-red-100'
                : 'bg-amber-50 border-amber-200 hover:bg-amber-100'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className={`text-sm font-medium truncate ${
                  a.isOverdue ? 'text-red-900' : 'text-amber-900'
                }`}
              >
                {a.clientName}
                {a.requestNumber && (
                  <span className="ml-1.5 font-normal text-xs opacity-70">№{a.requestNumber}</span>
                )}
              </span>
              <span
                className={`shrink-0 text-xs font-medium ${
                  a.isOverdue ? 'text-red-700' : 'text-amber-700'
                }`}
              >
                {new Date(a.dueAt).toLocaleDateString('ru-RU')}
                {a.isOverdue ? ' — просрочен' : ' — сегодня'}
              </span>
            </div>
            {(a.note || showManager) && (
              <div
                className={`mt-0.5 flex items-center justify-between gap-2 text-xs ${
                  a.isOverdue ? 'text-red-700' : 'text-amber-700'
                }`}
              >
                <span className="truncate">{a.note ?? ''}</span>
                {showManager && <span className="shrink-0 opacity-70">{a.managerName ?? '—'}</span>}
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  )
}
